import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import ReactWordcloud from "react-wordcloud";
import "tippy.js/dist/tippy.css";
import "tippy.js/animations/scale.css";

interface WordCloudDisplayProps {
  words: Array<{ text: string; value: number }>;
  isLoading?: boolean;
}

export function WordCloudDisplay({ words, isLoading }: WordCloudDisplayProps) {
  const options = {
    rotations: 2,
    rotationAngles: [0, 90] as [number, number],
    fontSizes: [16, 80] as [number, number],
    padding: 4,
    scale: "log" as const,
    spiral: "archimedean" as const,
    enableTooltip: true,
    deterministic: true,
    fontFamily: "Inter, system-ui, sans-serif",
    fontWeight: "600",
    colors: [
      "#3b82f6", // blue-500
      "#8b5cf6", // violet-500
      "#ec4899", // pink-500
      "#f59e0b", // amber-500
      "#10b981", // emerald-500
      "#6366f1", // indigo-500
    ],
  };

  const callbacks = {
    getWordTooltip: (word: { text: string; value: number }) =>
      `${word.text}: ${word.value} occurrences`,
  };

  if (isLoading) {
    return (
      <Card className={cn("h-[600px]", isLoading && "animate-pulse")}>
        <CardContent className="flex h-full items-center justify-center p-6">
          <div className="h-full w-full rounded bg-gray-200" />
        </CardContent>
      </Card>
    );
  }

  if (!words || words.length === 0) {
    return (
      <Card className="h-[600px]">
        <CardContent className="flex h-full items-center justify-center p-6">
          <div className="text-center">
            <p className="text-lg text-gray-500">No messages found</p>
            <p className="text-sm text-gray-400">Try adjusting your filters</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-[600px]">
      <CardContent className="h-full p-6">
        <ReactWordcloud words={words} options={options} callbacks={callbacks} />
      </CardContent>
    </Card>
  );
}
